import { useState } from "react";
import { ColorPicker } from "./ColorPicker";
import { updateWidgetConfig } from "../api";
import type { ConfigPanelProps } from "./component-types";

export function ConfigPanel({
  settings,
  onSettingsChange,
  onClose,
}: ConfigPanelProps) {
  const [activeTab, setActiveTab] = useState<"general" | "colors">("general");
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  async function handleSave() {
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const result = await updateWidgetConfig(settings.websiteId, settings);

      if (!result || !result.success) {
        setSaveError(result?.error || "Failed to save configuration");
        return;
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : "Failed to save configuration"
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="container">
      <div className="header">
        <h1>Widget Settings</h1>
        <p>Customize your AI chatbot appearance</p>
      </div>

      <div className="tabs">
        <button
          className={`tab ${activeTab === "general" ? "active" : ""}`}
          onClick={() => setActiveTab("general")}
        >
          General
        </button>
        <button
          className={`tab ${activeTab === "colors" ? "active" : ""}`}
          onClick={() => setActiveTab("colors")}
        >
          Colors
        </button>
      </div>

      {activeTab === "general" && (
        <div>
          <div className="form-group">
            <label className="form-label">Bot Name</label>
            <input
              type="text"
              className="form-input"
              value={settings.botName}
              onChange={(e) =>
                onSettingsChange({ ...settings, botName: e.target.value })
              }
              placeholder="Assistant"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Desktop Position</label>
            <select
              className="form-select"
              value={settings.desktopPosition}
              onChange={(e) =>
                onSettingsChange({
                  ...settings,
                  desktopPosition: e.target.value as any,
                })
              }
            >
              <option value="bottom-right">Bottom Right</option>
              <option value="bottom-left">Bottom Left</option>
              <option value="bottom-center">Bottom Center</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Mobile Position</label>
            <select
              className="form-select"
              value={settings.mobilePosition}
              onChange={(e) =>
                onSettingsChange({
                  ...settings,
                  mobilePosition: e.target.value as any,
                })
              }
            >
              <option value="bottom-right">Bottom Right</option>
              <option value="bottom-left">Bottom Left</option>
              <option value="bottom-center">Bottom Center</option>
            </select>
          </div>
        </div>
      )}

      {activeTab === "colors" && (
        <ColorPicker settings={settings} onSettingsChange={onSettingsChange} />
      )}

      <div className="divider" />

      {saveError && (
        <div
          style={{
            padding: "12px",
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: "6px",
            marginBottom: "16px",
            fontSize: "13px",
            color: "#991b1b",
          }}
        >
          {saveError}
        </div>
      )}

      {saveSuccess && (
        <div
          style={{
            padding: "12px",
            background: "#f0fdf4",
            border: "1px solid #bbf7d0",
            borderRadius: "6px",
            marginBottom: "16px",
            fontSize: "13px",
            color: "#166534",
          }}
        >
          Settings saved successfully!
        </div>
      )}

      <div style={{ display: "flex", gap: "12px" }}>
        <button className="button button-secondary" onClick={onClose}>
          Cancel
        </button>
        <button
          className="button button-primary"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? "Saving..." : "Save Settings"}
        </button>
      </div>
    </div>
  );
}
